/**
 * FlashcardDeck Entity
 * Represents a collection of flashcards for a course lesson
 *
 * DB Table: FlashcardDecks
 * - id (PK)
 * - lesson_id (FK, nullable for merged decks)
 * - course_id (FK)
 * - created_by (FK to users)
 * - creator_role
 * - workspace_id (FK)
 * - title
 * - description
 * - card_count (denormalized, auto-updated by trigger)
 * - is_ai_generated
 * - is_merged
 * - source_deck_ids
 */
export interface FlashcardDeckProps {
  id: string;
  lessonId: string | null;
  courseId: string;
  createdBy: string;
  creatorRole: string;
  workspaceId: string;
  title: string;
  description: string;
  cardCount: number;
  isAiGenerated: boolean;
  isMerged: boolean;
  sourceDeckIds: string[];
  createdAt: Date;
  updatedAt: Date;
  // Computed/joined fields
  courseName?: string;
  lessonTitle?: string;
  creatorName?: string;
  dueCardCount?: number;
  masteredCount?: number;
  newCardCount?: number;
}

export class FlashcardDeck {
  private readonly props: FlashcardDeckProps;

  constructor(props: FlashcardDeckProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get lessonId(): string | null {
    return this.props.lessonId;
  }

  get courseId(): string {
    return this.props.courseId;
  }

  get createdBy(): string {
    return this.props.createdBy;
  }

  get creatorRole(): string {
    return this.props.creatorRole;
  }

  get workspaceId(): string {
    return this.props.workspaceId;
  }

  get title(): string {
    return this.props.title;
  }

  get description(): string {
    return this.props.description;
  }

  get cardCount(): number {
    return this.props.cardCount;
  }

  get isAiGenerated(): boolean {
    return this.props.isAiGenerated;
  }

  get isMerged(): boolean {
    return this.props.isMerged;
  }

  get sourceDeckIds(): string[] {
    return this.props.sourceDeckIds;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  get courseName(): string | undefined {
    return this.props.courseName;
  }

  get lessonTitle(): string | undefined {
    return this.props.lessonTitle;
  }

  get creatorName(): string | undefined {
    return this.props.creatorName;
  }

  get dueCardCount(): number {
    return this.props.dueCardCount ?? 0;
  }

  get masteredCount(): number {
    return this.props.masteredCount ?? 0;
  }

  get newCardCount(): number {
    return this.props.newCardCount ?? 0;
  }

  isEmpty(): boolean {
    return this.props.cardCount === 0;
  }

  hasDueCards(): boolean {
    return this.dueCardCount > 0;
  }

  getMasteryPercentage(): number {
    if (this.props.cardCount === 0) return 0;
    return Math.round((this.masteredCount / this.props.cardCount) * 100);
  }

  belongsToCourse(courseId: string): boolean {
    return this.props.courseId === courseId;
  }

  isOwnedBy(userId: string): boolean {
    return this.props.createdBy === userId;
  }

  toPlainObject(): FlashcardDeckProps {
    return { ...this.props };
  }
}
